import { useState, useEffect, useCallback, useRef } from 'react';

// Oracle 서버 API URL — Vercel 배포 시 REACT_APP_API_URL 환경변수로 설정
// 로컬 개발: 빈 문자열 (proxy로 localhost:8000 사용)
const API_BASE = process.env.REACT_APP_API_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';

/** 공통 헤더 (API 키 인증) */
function getHeaders() {
  const h = { 'Content-Type': 'application/json' };
  if (API_KEY) h['X-API-Key'] = API_KEY;
  return h;
}

/** API_BASE 포함 전체 URL 생성 */
export function apiUrl(endpoint) {
  return `${API_BASE}${endpoint}`;
}

/** 공통 fetch wrapper (API_BASE + 헤더 자동 적용) */
export async function apiFetch(endpoint, options = {}) {
  const url = apiUrl(endpoint);
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (options.signal?.aborted) abort();
  options.signal?.addEventListener('abort', abort);
  const timeout = endpoint === '/api/fundamental/all'
    && (options.method || 'GET').toUpperCase() === 'GET'
    ? setTimeout(abort, 20000) : null;
  try {
    const res = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: { ...getHeaders(), ...options.headers },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener('abort', abort);
  }
}

export function useApi(endpoint, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const json = await apiFetch(endpoint);
      setData(json);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line
  }, [fetchData, ...deps]);

  return { data, loading, error, refetch: fetchData };
}

export function usePollingApi(endpoint, intervalMs = 60000, deps = []) {
  const api = useApi(endpoint, deps);
  useEffect(() => {
    const id = setInterval(() => api.refetch(), intervalMs);
    return () => clearInterval(id);
  // eslint-disable-next-line
  }, [intervalMs]);
  return api;
}

export function useWebSocket(url = '/ws/live') {
  const [lastMessage, setLastMessage] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    // WebSocket URL: API_BASE가 있으면 그 호스트 사용, 없으면 현재 호스트
    let wsUrl;
    if (API_BASE) {
      const parsed = new URL(API_BASE);
      const wsProt = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${wsProt}//${parsed.host}${url}`;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}${url}`;
    }

    let reconnectTimer;

    function connect() {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        reconnectTimer = setTimeout(connect, 5000);
      };
      ws.onmessage = (e) => {
        try { setLastMessage(JSON.parse(e.data)); } catch {}
      };
    }

    connect();
    return () => {
      clearTimeout(reconnectTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [url]);

  return { lastMessage, connected };
}

// KST 변환 유틸
export function toKST(dateStr) {
  if (!dateStr) return null;
  // 서버가 이미 KST로 보내는 경우 그대로 사용
  return new Date(dateStr);
}

const kstFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Seoul',
  month: 'numeric', day: 'numeric',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

export function fmtKST(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  // Explicit Seoul time also covers browsers outside Korea; h23 keeps midnight 00.
  const parts = Object.fromEntries(kstFormatter.formatToParts(d).map(p => [p.type, p.value]));
  return `${parts.month}/${parts.day} ${parts.hour}:${parts.minute}`;
}

/** NEX-1336: 게이트가 닫히면 백엔드가 손익·수익률을 null로 준다. 0으로 보이면 안 된다. */
export const PNL_HOLD = '산출 보류';
export function holdOr(v, fmt) {
  return v == null ? PNL_HOLD : fmt(v);
}

export function fmtNum(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString('ko-KR', { maximumFractionDigits: 0 });
}

/** 시장별 가격 포맷: US → $1,234.56, KR → ₩1,234 */
export function fmtPrice(n, market) {
  if (n == null) return '0';
  if (market === 'US') {
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return '₩' + Number(n).toLocaleString('ko-KR', { maximumFractionDigits: 0 });
}

// 시장 상태 계산 (클라이언트 측 KST 기준)
export function getMarketStatus() {
  const now = new Date();
  const kst = new Date(now.getTime() + (9 * 60 - now.getTimezoneOffset()) * 60000);
  const weekday = kst.getDay(); // 0=일 6=토
  const h = kst.getHours();
  const m = kst.getMinutes();
  const t = h * 60 + m;
  const isWeekday = weekday >= 1 && weekday <= 5;

  const krOpen = isWeekday && t >= 540 && t < 930;

  let usOpen = false;
  if (weekday === 6) { // 토
    usOpen = t < 360;
  } else if (weekday === 0) { // 일
    usOpen = false;
  } else if (weekday === 1) { // 월
    usOpen = t >= 1350;
  } else { // 화~금
    usOpen = t < 360 || t >= 1350;
  }

  return {
    krOpen, usOpen,
    krLabel: krOpen ? 'KR 장중' : 'KR 마감',
    usLabel: usOpen ? 'US 장중' : 'US 마감',
    kstTime: `${kst.getFullYear()}-${String(kst.getMonth()+1).padStart(2,'0')}-${String(kst.getDate()).padStart(2,'0')} ${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`,
  };
}
